import React, { useState, useEffect, forwardRef, useImperativeHandle, useMemo } from "react";
import {
  Box,
  FormControl,
  TextField,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { useMsal } from "@azure/msal-react";
import { getApiToken } from "../authUtils";
import UserSelect from "../helpers/UserSelect";
import ContactDetailsFields, {
  EMPTY_CONTACT,
  ContactSummary,
  cleanContacts,
  contactSearchText,
  hasInvalidContacts,
  toContactForm,
} from "../helpers/ContactDetailsFields";
const urlconfig = require("../config/urlconfig");

const EMPTY_FORM = {
  legalVendorName: "",
  vendorAddress: "",
  vendorState: "",
  vendorGST: "",
  contacts: [EMPTY_CONTACT],
  cScmPoc: "",
};

const GST_STATES = [
  { code: "01", name: "Jammu & Kashmir(01)" },
  { code: "02", name: "Himachal Pradesh(02)" },
  { code: "03", name: "Punjab(03)" },
  { code: "04", name: "Chandigarh(04)" },
  { code: "05", name: "Uttarakhand(05)" },
  { code: "06", name: "Haryana(06)" },
  { code: "07", name: "Delhi(07)" },
  { code: "08", name: "Rajasthan(08)" },
  { code: "09", name: "Uttar Pradesh(09)" },
  { code: "10", name: "Bihar(10)" },
  { code: "11", name: "Sikkim(11)" },
  { code: "12", name: "Arunachal Pradesh(12)" },
  { code: "13", name: "Nagaland(13)" },
  { code: "14", name: "Manipur(14)" },
  { code: "15", name: "Mizoram(15)" },
  { code: "16", name: "Tripura(16)" },
  { code: "17", name: "Meghalaya(17)" },
  { code: "18", name: "Assam(18)" },
  { code: "19", name: "West Bengal(19)" },
  { code: "20", name: "Jharkhand(20)" },
  { code: "21", name: "Odisha(21)" },
  { code: "22", name: "Chhattisgarh(22)" },
  { code: "23", name: "Madhya Pradesh(23)" },
  { code: "24", name: "Gujarat(24)" },
  { code: "26", name: "Dadra & Nagar Haveli and Daman & Diu(26)" },
  { code: "27", name: "Maharashtra(27)" },
  { code: "28", name: "Andhra Pradesh(28)" },
  { code: "29", name: "Karnataka(29)" },
  { code: "30", name: "Goa(30)" },
  { code: "31", name: "Lakshadweep(31)" },
  { code: "32", name: "Kerala(32)" },
  { code: "33", name: "Tamil Nadu(33)" },
  { code: "34", name: "Puducherry(34)" },
  { code: "35", name: "Andaman & Nicobar Islands(35)" },
  { code: "36", name: "Telangana(36)" },
  { code: "37", name: "Andhra Pradesh(37)" },
  { code: "38", name: "Ladakh(38)" }
];

// props.onVendorMapped is an optional callback UniqueVendors passes in
// so it can refresh its list right after a raw name gets mapped/unmapped.
const VendorRegistration = forwardRef((props, ref) => {
  const { instance, accounts } = useMsal();
  const account = accounts?.[0];

  const [vendorForm, setVendorForm] = useState(EMPTY_FORM);
  const [vendorTable, setVendorTable] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [gstError, setGstError] = useState("");
  const [nameError, setNameError] = useState("");
  const [editingVendorId, setEditingVendorId] = useState(null);
  const [originalVendor, setOriginalVendor] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [pendingRawName, setPendingRawName] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  // Inline SCM POC edit in the vendor table: one row at a time
  const [scmPocEdit, setScmPocEdit] = useState({ id: null, value: "", saving: false });

  // "new" = create a brand new vendor, "existing" = map/move the raw name
  // onto an already-registered vendor. Only relevant when pendingRawName is set.
  const [mappingMode, setMappingMode] = useState("new");
  const [selectedExistingVendor, setSelectedExistingVendor] = useState(null);

  // Set only when editing an ALREADY-mapped raw name (via openEditMapping).
  // Lets us know which vendor to remove the raw name from if it gets moved/unmapped.
  const [originalMappedVendorId, setOriginalMappedVendorId] = useState(null);

  // Exposes imperative methods so UniqueVendors.js can do:
  //   vendorRegRef.current.openWithPrefill(rawName)              — for unmapped names
  //   vendorRegRef.current.openEditMapping(rawName, vendorId)     — for already-mapped names
  useImperativeHandle(ref, () => ({
    openWithPrefill: (rawName) => {
      setVendorForm({ ...EMPTY_FORM, legalVendorName: rawName });
      setPendingRawName(rawName);
      setEditingVendorId(null);
      setOriginalVendor(null);
      setSelectedExistingVendor(null);
      setOriginalMappedVendorId(null);
      // Default to "existing" if there are vendors already registered —
      // most raw names turn out to be variants of a vendor that already exists.
      setMappingMode(vendorTable.length > 0 ? "existing" : "new");
      setOpenModal(true);
    },
    openEditMapping: (rawName, currentVendorId) => {
      const current = vendorTable.find((v) => v.id === currentVendorId) || null;
      setVendorForm(EMPTY_FORM);
      setPendingRawName(rawName);
      setEditingVendorId(null);
      setOriginalVendor(null);
      setMappingMode("existing");
      setSelectedExistingVendor(current);
      setOriginalMappedVendorId(currentVendorId);
      setOpenModal(true);
    },
  }));

  const showSnackbar = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const sendAuthenticatedRequest = async (url, method, body) => {
    const token = await getApiToken(instance, account);
    const res = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: body ? JSON.stringify(body) : undefined
    });
    const text = await res.text();
    if (!res.ok) throw new Error(text || "API failed");
    try { return text ? JSON.parse(text) : {}; } catch { return text; }
  };

  // ─── Handlers ────────────────────────────────────────────────────────────────

  const validateGST = (gst, state) => {
    if (!gst) return "";

    gst = gst.trim().toUpperCase();

    const regex =
      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

    if (!regex.test(gst))
      return "Invalid GSTIN";

    const gstState = GST_STATES.find(
      s => s.code === gst.substring(0, 2)
    );

    if (!gstState)
      return "Invalid GST State Code";

    if (state && gstState.name !== state)
      return `GST belongs to ${gstState.name}`;

    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setVendorForm((prev) => ({ ...prev, [name]: value }));

    if (name === "legalVendorName") {
      const exists = vendorTable.some(
        v => v.legalVendorName?.trim().toLowerCase() === value.trim().toLowerCase() &&
             v.id !== editingVendorId
      );
      setNameError(exists ? "Legal Vendor Name already exists" : "");
    }

    if (name === "vendorGST") {

      const gst = value.toUpperCase();

      const exists = !!gst && vendorTable.some(
        v =>
          v.vendorGST?.toUpperCase() === gst &&
          v.id !== editingVendorId
      );

      if (name === "legalVendorName") {
        const exists = vendorTable.some(
          v => v.legalVendorName?.trim().toLowerCase() === value.trim().toLowerCase() &&
              v.id !== editingVendorId
        );
        setNameError(exists ? "Legal Vendor Name already exists" : "");
      }

      if (exists) {
        setGstError("GST Already Exists");
      } else {
        setGstError(
          validateGST(gst, vendorForm.vendorState)
        );
      }
    }

  };

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingVendorId(null);
    setOriginalVendor(null);
    setGstError("");
    setNameError("");
    setVendorForm(EMPTY_FORM);
    setPendingRawName(null);
    setMappingMode("new");
    setSelectedExistingVendor(null);
    setOriginalMappedVendorId(null);
  };

  const isVendorChanged = () =>
    !!originalVendor && JSON.stringify(originalVendor) !== JSON.stringify(vendorForm);

  // ─── API calls ───────────────────────────────────────────────────────────────

  const fetchVendors = async () => {
    try {
      const data = await sendAuthenticatedRequest(
        `${urlconfig.backend_api_base_url}/api/v2/vendors`,
        "GET"
      );
      setVendorTable(data.sort((a, b) => b.id - a.id));
    } catch (error) {
      showSnackbar("Failed to fetch vendors", "error");
    }
  };

  const handleUpdateVendor = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await sendAuthenticatedRequest(
        `${urlconfig.backend_api_base_url}/api/v2/vendors/${editingVendorId}`,
        "PUT",
        {
          legalVendorName: vendorForm.legalVendorName,
          vendorAddress: vendorForm.vendorAddress,
          vendorState: vendorForm.vendorState,
          vendorGST: vendorForm.vendorGST,
          contacts: cleanContacts(vendorForm.contacts),
          cScmPoc: vendorForm.cScmPoc,
        }
      );
      showSnackbar("Vendor updated successfully");
      handleCloseModal();
      fetchVendors();
    } catch(err) {
      const msg = err.message || "";
      showSnackbar(
        msg.startsWith("Contact ") ? msg :
          msg.includes("Legal Vendor Name") ? "Legal Vendor Name already exists" :
        msg.includes("GST") ? "GST already exists" :
        "Failed to create vendor",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Plain "+ Create Vendor" flow — no raw name involved at all.
  const handleCreateVendorOnly = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await sendAuthenticatedRequest(
        `${urlconfig.backend_api_base_url}/api/v2/vendors`,
        "POST",
        {
          legalVendorName: vendorForm.legalVendorName,
          vendorAddress: vendorForm.vendorAddress,
          vendorState: vendorForm.vendorState,
          vendorGST: vendorForm.vendorGST,
          contacts: cleanContacts(vendorForm.contacts),
          cScmPoc: vendorForm.cScmPoc,
          rawVendorNames: [],
        }
      );
      showSnackbar("Vendor created successfully");
      handleCloseModal();
      fetchVendors();
    } catch (err) {
      const msg = err.message || "";
        showSnackbar(
          msg.startsWith("Contact ") ? msg :
          msg.includes("Legal Vendor Name") ? "Legal Vendor Name already exists" :
          msg.includes("GST") ? "GST already exists" :
          "Failed to create vendor",
          "error"
        );
    } finally {
      setSubmitting(false);
    }
  };

  // Unified mapping commit: handles map-to-new, map-to-existing, AND moving
  // a raw name from one vendor to another (when originalMappedVendorId is set
  // and the target differs).
  const commitMapping = async () => {
    if (!pendingRawName) return;
    setSubmitting(true);
    try {
      let targetVendorId;

      if (mappingMode === "existing") {
        if (!selectedExistingVendor) return;
        await sendAuthenticatedRequest(
          `${urlconfig.backend_api_base_url}/api/v2/vendors/${selectedExistingVendor.id}`,
          "PUT",
          { rawVendorNames: [pendingRawName] }
        );
        targetVendorId = selectedExistingVendor.id;
      } else {
        const created = await sendAuthenticatedRequest(
          `${urlconfig.backend_api_base_url}/api/v2/vendors`,
          "POST",
          {
            legalVendorName: vendorForm.legalVendorName,
            vendorAddress: vendorForm.vendorAddress,
            vendorState: vendorForm.vendorState,
            vendorGST: vendorForm.vendorGST,
            contacts: cleanContacts(vendorForm.contacts),
            cScmPoc: vendorForm.cScmPoc,
            rawVendorNames: [pendingRawName],
          }
        );
        targetVendorId = created.id;
      }

      // If this raw name was previously mapped to a DIFFERENT vendor, clean up the old mapping
      if (originalMappedVendorId && originalMappedVendorId !== targetVendorId) {
        await sendAuthenticatedRequest(
          `${urlconfig.backend_api_base_url}/api/v2/vendors/${originalMappedVendorId}/raw-names?name=${encodeURIComponent(pendingRawName)}`,
          "DELETE"
        );
      }

      showSnackbar(originalMappedVendorId ? "Mapping updated" : "Vendor mapped successfully");
      handleCloseModal();
      fetchVendors();
      props.onVendorMapped?.();
    } catch (err) {
      showSnackbar(err.message || "Failed to save mapping", "error");
    } finally {
      setSubmitting(false);
    }
  };

  // Removes the raw name from its current vendor entirely — no replacement mapping.
  const handleUnmap = async () => {
    if (!originalMappedVendorId || !pendingRawName) return;
    setSubmitting(true);
    try {
      await sendAuthenticatedRequest(
        `${urlconfig.backend_api_base_url}/api/v2/vendors/${originalMappedVendorId}/raw-names?name=${encodeURIComponent(pendingRawName)}`,
        "DELETE"
      );
      showSnackbar(`Unmapped "${pendingRawName}"`);
      handleCloseModal();
      fetchVendors();
      props.onVendorMapped?.();
    } catch (err) {
      showSnackbar(err.message || "Failed to unmap", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const startScmPocEdit = (vendor) =>
    setScmPocEdit({ id: vendor.id, value: vendor.cScmPoc || "", saving: false });

  const cancelScmPocEdit = () => setScmPocEdit({ id: null, value: "", saving: false });

  // Sends only cScmPoc; the update endpoint leaves every other field (and the contacts) as they are
  const saveScmPoc = async () => {
    const { id, value } = scmPocEdit;
    setScmPocEdit((prev) => ({ ...prev, saving: true }));
    try {
      const updated = await sendAuthenticatedRequest(
        `${urlconfig.backend_api_base_url}/api/v2/vendors/${id}`,
        "PUT",
        { cScmPoc: value }
      );
      setVendorTable((prev) => prev.map((v) => (v.id === id ? { ...v, ...updated } : v)));
      showSnackbar("SCM person updated");
      cancelScmPocEdit();
    } catch (err) {
      showSnackbar(err.message || "Failed to update SCM person", "error");
      setScmPocEdit((prev) => ({ ...prev, saving: false }));
    }
  };

  const handleDeleteVendor = async (id) => {
    try {
      await sendAuthenticatedRequest(
        `${urlconfig.backend_api_base_url}/api/v2/vendors/${id}`,
        "DELETE"
      );
      showSnackbar("Vendor deleted");
      fetchVendors();
    } catch (err) {
      let message = "Delete failed";
      if (err.message?.includes("attached to one or more")) {
        message = "Deletion not allowed — this vendor is linked to existing cost sheets.";
      } else if (err.message?.includes("linked to") && err.message?.includes("raw vendor name")) {
        message = "Can't delete — this vendor is linked to a raw vendor name. Unmap it first.";
      }
      showSnackbar(message, "error");
    }
  };

  useEffect(() => { fetchVendors(); }, []);

  const isPrimaryDisabled = () => {
    if (submitting) return true;

    if (pendingRawName) {
      if (mappingMode === "existing") {
        if (!selectedExistingVendor) return true;
        // No-op if re-selecting the same vendor it's already mapped to
        if (originalMappedVendorId && selectedExistingVendor.id === originalMappedVendorId) return true;
        return false;
      }
      // mode "new"
      return (
        !vendorForm.legalVendorName ||
        !vendorForm.vendorState ||
        hasInvalidContacts(vendorForm.contacts) ||
        !!gstError || !!nameError
      );
    }

    return (
      !vendorForm.legalVendorName ||
      !vendorForm.vendorState ||
      hasInvalidContacts(vendorForm.contacts) ||
      !!gstError ||
      !!nameError ||
      (editingVendorId ? !isVendorChanged() : false)
    );
  };

  const handlePrimaryAction = async (e) => {
    if (pendingRawName) {
      await commitMapping();
    } else if (editingVendorId) {
      await handleUpdateVendor(e);
    } else {
      await handleCreateVendorOnly(e);
    }
  };

  const primaryLabel = () => {
    if (submitting) return "Saving...";
    if (pendingRawName) return originalMappedVendorId ? "Save Mapping" : "Map Vendor";
    return editingVendorId ? "Update" : "Create";
  };

  const filteredVendors = useMemo(() => {
    const search = searchTerm.toLowerCase().trim();

    if (!search) return vendorTable;

    return vendorTable.filter((vendor) =>
      [
        vendor.legalVendorName,
        vendor.vendorGST,
        contactSearchText(vendor, vendor.vendorSpoc, vendor.vendorSpocNumber),
        vendor.cScmPoc,
      ]
        .filter(Boolean)
        .some((field) =>
          field.toString().toLowerCase().includes(search)
        )
    );
  }, [vendorTable, searchTerm]);

      useEffect(() => {
      setPage(0);
    }, [searchTerm]);

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 4, background: "#f5f7fb", minHeight: "100vh" }}>
      <Typography variant="h4" mb={3} fontWeight="bold">
        Vendor Management
      </Typography>

      <Paper sx={{ p: 3, borderRadius: 3, boxShadow: "0 8px 20px rgba(0,0,0,0.08)" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="h6">Vendor List</Typography>

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <TextField
              size="small"
              placeholder="Search Vendor / GST / Contact / SCM"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: "gray" }} />,
              }}
              sx={{ minWidth: 320 }}
            />

            <Button
              variant="contained"
              onClick={() => setOpenModal(true)}
              sx={{ textTransform: "none", borderRadius: 2, fontWeight: "bold" }}
            >
              + Create Vendor
            </Button>
          </Box>
        </Box>

        <Table>
          <TableHead>
            <TableRow sx={{ background: "#f1f3f6" }}>
              <TableCell><b>Legal Vendor Name</b></TableCell>
              <TableCell><b>Address / State</b></TableCell>
              <TableCell><b>GST</b></TableCell>
              <TableCell><b>Contacts</b></TableCell>
              <TableCell><b>SCM Person</b></TableCell>
              <TableCell align="center"><b>Actions</b></TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredVendors.length > 0 ? (
              filteredVendors
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((v) => (
                  <TableRow key={v.id} hover>
                    <TableCell>
                      {v.legalVendorName}
                      {v.rawVendorNames?.length > 0 && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {v.rawVendorNames.length} raw name{v.rawVendorNames.length === 1 ? "" : "s"} mapped
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{v.vendorAddress} - {v.vendorState}</TableCell>
                    <TableCell>{v.vendorGST || '—'}</TableCell>
                    <TableCell>
                      <ContactSummary contacts={v.contacts} legacyPerson={v.vendorSpoc} legacyPhone={v.vendorSpocNumber} />
                    </TableCell>
                    <TableCell sx={{ minWidth: scmPocEdit.id === v.id ? 280 : undefined }}>
                      {scmPocEdit.id === v.id ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Box sx={{ flexGrow: 1 }}>
                            <UserSelect
                              label="SCM Person"
                              value={scmPocEdit.value}
                              onChange={(value) => setScmPocEdit((prev) => ({ ...prev, value }))}
                            />
                          </Box>
                          <IconButton
                            size="small"
                            color="primary"
                            aria-label="Save SCM person"
                            onClick={saveScmPoc}
                            disabled={scmPocEdit.saving || scmPocEdit.value === (v.cScmPoc || "")}
                          >
                            <CheckIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            aria-label="Cancel SCM person edit"
                            onClick={cancelScmPocEdit}
                            disabled={scmPocEdit.saving}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <span>{v.cScmPoc || "-"}</span>
                          <IconButton
                            size="small"
                            aria-label="Edit SCM person"
                            onClick={() => startScmPocEdit(v)}
                            disabled={scmPocEdit.id !== null}
                          >
                            <EditOutlinedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ display: "flex" }}>
                      <IconButton
                        aria-label="Edit vendor"
                        onClick={() => {
                          const selectedVendor = {
                            legalVendorName: v.legalVendorName || "",
                            vendorAddress: v.vendorAddress || "",
                            vendorState: v.vendorState || "",
                            vendorGST: v.vendorGST || "",
                            contacts: toContactForm(v.contacts, v.vendorSpoc, v.vendorSpocNumber),
                            cScmPoc: v.cScmPoc || ""
                          };
                          setVendorForm(selectedVendor);
                          setOriginalVendor(selectedVendor);
                          setEditingVendorId(v.id);
                          setPendingRawName(null);
                          setMappingMode("new");
                          setOpenModal(true);
                        }}
                        sx={{ background: "#e3f2fd", mr: 1, "&:hover": { background: "#bbdefb" } }}
                      >
                        <EditOutlinedIcon color="primary" />
                      </IconButton>
                      <IconButton
                        aria-label="Delete vendor"
                        onClick={() => handleDeleteVendor(v.id)}
                        sx={{ background: "#ffe5e5", "&:hover": { background: "#ffcccc" } }}
                      >
                        <DeleteIcon color="error" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">No Vendors Found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={filteredVendors.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* MODAL */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          {pendingRawName
            ? originalMappedVendorId
              ? `Edit Mapping for "${pendingRawName}"`
              : `Map "${pendingRawName}"`
            : editingVendorId
              ? "Edit Vendor"
              : "Add New Vendor"}
        </DialogTitle>

        <DialogContent dividers>
          {/* Only show the New/Existing toggle when mapping a raw name */}
          {pendingRawName && (
            <Box sx={{ mb: 3 }}>
              {originalMappedVendorId && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Currently mapped to <strong>{vendorTable.find(v => v.id === originalMappedVendorId)?.legalVendorName || "—"}</strong>.
                  Choose a different vendor below to move it, or use Unmap to remove the mapping entirely.
                </Alert>
              )}
              <ToggleButtonGroup
                value={mappingMode}
                exclusive
                fullWidth
                onChange={(e, val) => val && setMappingMode(val)}
                sx={{ mb: 1 }}
              >
                <ToggleButton value="existing" disabled={vendorTable.length === 0}>
                  {originalMappedVendorId ? "Move to Different Vendor" : "Map to Existing Vendor"}
                </ToggleButton>
                <ToggleButton value="new">
                  Create New Vendor
                </ToggleButton>
              </ToggleButtonGroup>
              {vendorTable.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  No vendors registered yet — you'll need to create one first.
                </Typography>
              )}
            </Box>
          )}

          {pendingRawName && mappingMode === "existing" ? (
            <FormControl fullWidth>
              <Autocomplete
                options={vendorTable}
                getOptionLabel={(option) => option.legalVendorName || ""}
                value={selectedExistingVendor}
                onChange={(_, value) => setSelectedExistingVendor(value)}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {option.legalVendorName}
                        {option.id === originalMappedVendorId && (
                          <Chip label="Current" size="small" sx={{ ml: 1, height: 18, fontSize: "0.65rem" }} />
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.vendorGST || 'No GST'} · {option.vendorState}
                        {option.rawVendorNames?.length > 0 && ` · ${option.rawVendorNames.length} raw name(s) mapped`}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Legal Vendor"
                    margin="dense"
                    autoFocus
                  />
                )}
              />
              {selectedExistingVendor && (
                <Box sx={{ mt: 2, p: 2, bgcolor: "#f8fafc", borderRadius: 2 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                    {selectedExistingVendor.legalVendorName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {selectedExistingVendor.vendorAddress} · {selectedExistingVendor.vendorState}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    GST: {selectedExistingVendor.vendorGST || 'Not registered'}
                  </Typography>
                  {selectedExistingVendor.rawVendorNames?.length > 0 && (
                    <Box sx={{ mt: 1, display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      {selectedExistingVendor.rawVendorNames.map((rn) => (
                        <Chip key={rn} label={rn} size="small" />
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </FormControl>
          ) : pendingRawName && mappingMode === "new" ? (
            <FormControl fullWidth>
              <TextField
                label="Legal Vendor Name"
                name="legalVendorName"
                value={vendorForm.legalVendorName || pendingRawName}
                onChange={handleChange}
                margin="dense"
                required
                error={!!nameError}
                helperText={nameError}
              />
              <TextField
                label="Address"
                name="vendorAddress"
                value={vendorForm.vendorAddress}
                onChange={handleChange}
                margin="dense"
                multiline
                rows={3}
              />
              <Autocomplete
                options={GST_STATES}
                getOptionLabel={(option) => option.name}
                value={GST_STATES.find(s => s.name === vendorForm.vendorState) || null}
                onChange={(_, value) => {
                  const selectedState = value?.name || "";
                  setVendorForm(prev => ({ ...prev, vendorState: selectedState }));
                  if (vendorForm.vendorGST) {
                    setGstError(validateGST(vendorForm.vendorGST, selectedState));
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} label="State" margin="dense" required />
                )}
              />
              <TextField
                label="GST"
                name="vendorGST"
                value={vendorForm.vendorGST}
                onChange={(e) => {
                  handleChange({ target: { name: "vendorGST", value: e.target.value.toUpperCase() } });
                }}
                inputProps={{ maxLength: 15 }}
                margin="dense"
                error={!!gstError}
                // GST is optional, but a vendor without it can't be picked on a cost sheet
                helperText={gstError || "Optional. Vendors without GST can't be used on cost sheets"}
              />
              <ContactDetailsFields
                contacts={vendorForm.contacts}
                onChange={(contacts) => setVendorForm((prev) => ({ ...prev, contacts }))}
                disabled={submitting}
              />
              <UserSelect
                label="Assign SCM Person"
                value={vendorForm.cScmPoc}
                onChange={(value) => handleChange({ target: { name: "cScmPoc", value } })}
              />
            </FormControl>
          ) : (
            <FormControl fullWidth>
              <TextField
                label="Legal Vendor Name"
                name="legalVendorName"
                value={vendorForm.legalVendorName}
                onChange={handleChange}
                margin="dense"
                required
                error={!!nameError}
                helperText={nameError}
              />
              <TextField
                label="Address"
                name="vendorAddress"
                value={vendorForm.vendorAddress}
                onChange={handleChange}
                margin="dense"
                multiline
                rows={3}
              />
              <Autocomplete
                options={GST_STATES}
                getOptionLabel={(option) => option.name}
                value={GST_STATES.find(s => s.name === vendorForm.vendorState) || null}
                onChange={(_, value) => {
                  const selectedState = value?.name || "";
                  setVendorForm(prev => ({ ...prev, vendorState: selectedState }));
                  if (vendorForm.vendorGST) {
                    setGstError(validateGST(vendorForm.vendorGST, selectedState));
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} label="State" margin="dense" required />
                )}
              />
              <TextField
                label="GST"
                name="vendorGST"
                value={vendorForm.vendorGST}
                onChange={(e) => {
                  handleChange({ target: { name: "vendorGST", value: e.target.value.toUpperCase() } });
                }}
                inputProps={{ maxLength: 15 }}
                margin="dense"
                error={!!gstError}
                // GST is optional, but a vendor without it can't be picked on a cost sheet
                helperText={gstError || "Optional. Vendors without GST can't be used on cost sheets"}
              />
              <ContactDetailsFields
                contacts={vendorForm.contacts}
                onChange={(contacts) => setVendorForm((prev) => ({ ...prev, contacts }))}
                disabled={submitting}
              />
              <UserSelect
                  label="Assign SCM Person"
                  value={vendorForm.cScmPoc}
                  onChange={(value) => handleChange({ target: { name: "cScmPoc", value } })}
                />
            </FormControl>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: originalMappedVendorId ? "space-between" : "flex-end", px: 3 }}>
          {originalMappedVendorId ? (
            <Button
              color="error"
              startIcon={<LinkOffIcon />}
              onClick={handleUnmap}
              disabled={submitting}
            >
              Unmap
            </Button>
          ) : <span />}
          <Box>
            <Button onClick={handleCloseModal} sx={{ mr: 1 }}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handlePrimaryAction}
              disabled={isPrimaryDisabled()}
            >
              {primaryLabel()}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* SNACKBAR */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
});

export default VendorRegistration;